import React, { useState } from "react";
import SideDrawer from "components/Nav/SideDrawer";
import TopNav from "components/Nav/TopNav";
import { ViewProvider } from "context/View";
import { AddressProvider } from "context/Address";
import { UserContext } from "context/User";
import Wizard from "components/Account/Wizard/Wizard";
import { CardContent, Button, Grid, CircularProgress, AppBar, Card, CssBaseline, Drawer, IconButton, Toolbar } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { makeAxiosCall } from "utils";
import { Menu, MenuOpen } from "@mui/icons-material";
import ApprovalExpirationPopup from "./utils/ApprovalExpirationPopup";
import { CartContext } from "context/Cart";
import logo from "assets/images/logo.svg";
import { canTakeSecondChance, isExpired, isLockedPath, secondChanceLockdown } from "utils/secondChance";

interface HomeProps {
    children: any,
}

export default function Home({ children }: HomeProps) {
    const { currentUser, signOut, fetchCurrentUser } = React.useContext(UserContext);
    const { clearCart } = React.useContext(CartContext);

    // const [hsCompanyProps, setCompanyProps] = React.useState({
    //   form: false,
    //   cert: false,
    //   agreement: false,
    // });

    const drawerWidth = 100;
    const navigate = useNavigate();
    const location = useLocation();

    const [open, setOpen] = useState(true);
    const [showExpirationPopup, setShowExpirationPopup] = React.useState<boolean>(false);
    const [existingResellerFlag, setExistingResellerFlag] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [mobileMode, setMobileMode] = useState(false);
    const [error, setError] = useState("");
    const [companyAdminEmails, setCompanyAdminEmails] = useState([]);
    const [reactivating, setReactivating] = useState(false);

    // "The reseller has done everything on their end" — every document submitted and
    // nothing bounced back for rework. What's left is Blizzard's review, which they
    // can't act on, so they shouldn't be parked on a checklist or nagged about a
    // deadline. Mirrors the cron's "are they waiting on us?" test in
    // check-expiring-companies.js. A denial reason pulls them back to the wizard.
    const approvalStatus = currentUser?.company?.approvalStatus;
    const approvalSubmitted =
        !!approvalStatus &&
        !!approvalStatus.infoFormComplete && !approvalStatus.infoFormDenialReason &&
        !!approvalStatus.certificateUploaded && !approvalStatus.certificateDenialReason &&
        !!approvalStatus.agreementComplete && !approvalStatus.agreementDenialReason;

    React.useEffect(() => {
        // Returning from a DocuSign embedded-signing session. This must live HERE
        // (not just the Wizard) because DocuSign returns signers to "/", and an
        // already-approved reseller re-signing (re-contract flow) sees the normal
        // dashboard at "/" — the Wizard never mounts, so it can't catch the param.
        // The API verifies the signature with DocuSign before changing anything.
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("event") === "signing_complete" || urlParams.get("ra") === "269394jdiepow494") {
            makeAxiosCall("post", "docusign/confirm-signing")
                .then(() => {
                    if (fetchCurrentUser) fetchCurrentUser();
                })
                .catch((err) => console.error("Could not confirm DocuSign signing:", err));
        }

        const mediaQuery = window.matchMedia("(min-width: 768px)");
        if (mediaQuery.matches) {
            handleDrawerOpen();
        } else {
            handleDrawerClose();
        }

        setMobileMode(!mediaQuery.matches);
        
        // mediaQuery.addListener((mq) => {
        //   if (mq.matches) {
        //     handleDrawerOpen();
        //   } else {
        //     handleDrawerClose();
        //   }
        // });
    }, []);

    React.useEffect(() => {
        if(currentUser && currentUser.company && !currentUser.company.approved && currentUser.accountLevel.name != "Admin"  && currentUser.accountLevel.name != "Super Admin") {
            loadCompanyAdminEmails();
        }

        if (currentUser && currentUser.accountSignupStatus) {
            setExistingResellerFlag(currentUser.accountSignupStatus == "needs review");
        }
        if (currentUser) {
            // Only warn companies that still have portal access (approved or
            // provisional) — disabled/expired companies keep their old
            // expiration date, and "your access is expiring" is wrong for them.
            const companyActive = currentUser.company?.approved || currentUser.company?.provisionalAccess;
            // Coming back from DocuSign, currentUser is still the PRE-signing snapshot
            // — confirm-signing hasn't cleared the expiration yet — so without this
            // guard the "your access is expiring, complete your requirements" modal
            // lands on the reseller seconds after they signed. Suppressed for anyone
            // who has submitted everything, for the same reason: nothing left to do.
            const params = new URLSearchParams(window.location.search);
            const returningFromSigning =
                params.get("event") === "signing_complete" || params.get("ra") === "269394jdiepow494";
            if (companyActive && currentUser.company?.approvalStatus?.expirationDate &&
                !returningFromSigning && !approvalSubmitted) {
                const warningPopupViewed = localStorage.getItem("warningPopupViewed");
                if (!warningPopupViewed && window.location.href.indexOf('approval') == -1) {
                    localStorage.setItem("warningPopupViewed", "true");
                    
                    openExpirationPopup();
                }
            }
        }
    }, [currentUser]);


    const loadCompanyAdminEmails = () => {
        makeAxiosCall(
            "get",
            `company-admin-emails`
        ).then(response => {
            console.log(response);
            
            setCompanyAdminEmails(response.data);
        }).catch(error => {
            console.error(error);
            setError("Something went wrong. Please refresh the page and try again.");
            setRequesting(false);
        });
    };

    const requestCompanyReview = () => {
        setRequesting(true);

        makeAxiosCall(
            "post",
            `account/${currentUser.uid}`,
            {
                accountSignupStatus: "needs review"
            }
        ).then(response => {
            if (response.status === 200) {
                makeAxiosCall(
                    "post",
                    "email/new-user",
                    {
                        to: {
                            id: currentUser.uid,
                            email: currentUser.email,
                        }
                    }
                );
                setExistingResellerFlag(true);
            } else {
                setError("Something went wrong. Please refresh the page and try again.");
            }
            setRequesting(false);
        }).catch(error => {
            console.error(error);
            setError("Something went wrong. Please refresh the page and try again.");
            setRequesting(false);
        });
    };

    // One-time self-serve reactivation: the API re-checks eligibility, pushes the
    // expiration out a month, marks the reason "(Second Chance)" and grants
    // provisional access so this same render lands them in the onboarding wizard.
    const takeSecondChance = () => {
        setReactivating(true);
        setError("");

        makeAxiosCall("post", "second-chance")
            .then(() => {
                if (fetchCurrentUser) fetchCurrentUser();
            })
            .catch((response) => {
                // makeAxiosCall rejects with error.response, and the API writes its
                // body with res.end() so it can arrive as an unparsed string.
                console.error(response);
                let message = "";
                try {
                    const body = typeof response?.data === "string" ? JSON.parse(response.data) : response?.data;
                    message = body?.error || "";
                } catch (e) {
                    message = "";
                }
                setError(message || "We couldn't reactivate your account. Please reach out to sales@blizzardpro.com.");
                setReactivating(false);
            });
    };

    const goToApplication = () => {
        window.scrollTo(0, 0);
        navigate("/apply");
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleMobileEvent = (type) => {
        const mediaQuery = window.matchMedia("(max-width: 768px)");
        if (mediaQuery.matches && type === "mobile") {
            setOpen(false);
        }
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const openExpirationPopup = () => {
        setShowExpirationPopup(true);
    };

    const closeExpirationPopup = () => {
        setShowExpirationPopup(false);
    };

    // Provisional resellers (mid-onboarding) get the full app shell so they can
    // browse products, place the qualifying order, and be walked through "My
    // Company" by the onboarding tour. The wizard itself is surfaced at "/" for
    // them (see onboardingInShell below) until they're fully approved.
    const bypassWizard =
        (
            currentUser.company != null &&
            currentUser.company.approved
        ) ||
        (currentUser.company != null && currentUser.company.provisionalAccess) ||
        currentUser.accountLevel.name === "Super Admin";

    // Bucket-A companies inside their one-time 30-day grace period can't reach the
    // catalog or the order pages — signing the agreement is their only outstanding
    // requirement, so leaving products open would let them ignore it for another
    // month. SideDrawer greys the same items out; this is the direct-URL backstop.
    const lockedBySecondChance =
        secondChanceLockdown(currentUser.company) && isLockedPath(location.pathname);

    const buildLockedCard = () => (
        <div className="overview view" style={{ display: "grid" }}>
            <Card raised={true} style={{ padding: 20, margin: "auto", display: "inline-block", marginTop: 40, maxWidth: 620 }}>
                <CardContent>
                    <h1 style={{ textAlign: "center" }}>
                        <i className="fad fa-lock" style={{ marginRight: 10 }}></i>
                        Locked until your agreement is signed
                    </h1>
                    <p style={{ textAlign: "center" }}>
                        Products and orders are on hold while your account is being reinstated.
                        Sign your reseller agreement to restore full portal access.
                    </p>
                    <Button
                        onClick={() => navigate("/")}
                        variant="contained"
                        style={{ width: "60%", marginTop: 30, marginRight: "auto", marginLeft: "auto", display: "block" }}
                        color="primary"
                        className="btn"
                    >
                        Go to My Checklist
                    </Button>
                </CardContent>
            </Card>
        </div>
    );

    // Show the onboarding wizard as the home-page body for a provisional reseller
    // who hasn't been fully approved yet — but keep the nav/shell so the tour can
    // navigate to the real /company and /products pages. Once every document is in
    // (approvalSubmitted) they get the normal dashboard instead: the remaining work
    // is Blizzard's countersignature + approval, and leaving them on a checklist of
    // green checkmarks reads as "still blocked" when they aren't. The checklist is
    // still reachable at /approval, and a denied document brings it back here.
    const onboardingInShell =
        currentUser.company != null &&
        !currentUser.company.approved &&
        currentUser.company.provisionalAccess &&
        !approvalSubmitted &&
        currentUser.accountLevel.name !== "Super Admin" &&
        location.pathname === "/";

    if (!currentUser.company) {
        // Fresh user, show how to join a company
        return (
            <div className="overview view" style={{ display: "grid" }}>
                <div className="top-floating-nav">
                    <div
                        className="back-crumb"
                        onClick={() => {
                            clearCart();
                            signOut();
                        }}
                    >
                        <i className="fad fa-sign-out"></i> Log Out
                    </div>
                </div>
                <img style={{ maxWidth: 150, margin: "auto", display: "inline-block", marginBottom: 20 }} className="logo" src={logo} alt="Blizzard pro" />
                <h1 style={{ textAlign: "center" }}>Thank you for signing up</h1>
                <Grid container>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card raised={true} style={{ padding: 10, margin: 10, display: "inline-block", height: "100%" }} >
                            <CardContent style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                <h1 style={{ textAlign: "center" }}>Existing Reseller</h1>
                                {existingResellerFlag ?
                                    (
                                        <>
                                            <p style={{ textAlign: "center" }}>
                                                You&apos;re all set for now!
                                            </p>
                                            <p style={{ textAlign: "center" }}>
                                                Our team will review your account and set up access
                                                to your company. A representative will contact you
                                                when your account has been verified.
                                            </p>
                                            <Button
                                                onClick={() => {
                                                    signOut();
                                                }}
                                                variant="contained"
                                                style={{ width: "48%", marginTop: "auto", marginBottom: 50, marginRight: "auto", marginLeft: "auto", display: "block" }}
                                                color="primary"
                                                className="btn"
                                            >
                                                Sign out
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <p style={{ textAlign: "center" }}>
                                                If your company is an existing Blizzard reseller, but new to the Blizzard Customer Portal click the link below to
                                                submit your account for review.
                                            </p>
                                            <p style={{ textAlign: "center" }}>A representative will contact you once your account has been verified.</p>
                                            <p style={{ textAlign: "center" }}>
                                                If your company already uses the Blizzard Customer Portal, have an admin user from
                                                your company send you an invite email.
                                            </p>
                                            {error.length > 0 && (
                                                <p style={{ textAlign: "center", color: "tomato" }}>
                                                    {error}
                                                </p>
                                            )}
                                            <Button
                                                onClick={() => {
                                                    requestCompanyReview();
                                                }}
                                                variant="contained"
                                                style={{ width: "48%", marginTop: "auto", marginBottom: 50, marginRight: "auto", marginLeft: "auto", display: "block" }}
                                                color="primary"
                                                className="btn"
                                            >
                                                {requesting ?
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={24}
                                                    /> :
                                                    "Request Review"
                                                }
                                            </Button>
                                        </>
                                    )
                                }
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card raised={true} style={{ padding: 10, margin: 10, display: "inline-block", height: "100%" }} >
                            <CardContent style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                <h1 style={{ textAlign: "center" }}>Become a Reseller</h1>

                                {currentUser.accountSignupStatus == "applied" ? (
                                    <>
                                        <p style={{ textAlign: "center" }}>
                                            Your application has been submitted.
                                            A representative will contact you with more information
                                            once your application has been reviewed.
                                        </p>
                                        <Button
                                            onClick={goToApplication}
                                            variant="contained"
                                            style={{ width: "48%", marginTop: "auto", marginBottom: 50, marginRight: "auto", marginLeft: "auto", display: "block" }}
                                            color="primary"
                                            className="btn"
                                        >
                                            Edit your Application
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <p style={{ textAlign: "center" }}>
                                            If your company is looking to become a reseller, click the
                                            link below and fill out our reseller application form. A representative
                                            will contact you with more information about the approval process.
                                        </p>
                                        <Button
                                            onClick={goToApplication}
                                            variant="contained"
                                            style={{ width: "48%", marginTop: "auto", marginBottom: 50, marginRight: "auto", marginLeft: "auto", display: "block" }}
                                            color="primary"
                                            className="btn"
                                        >
                                            Apply
                                        </Button>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </div>
        );
    } else if (!currentUser.company.fishbowlCustomerId) {
        // User company still under review
        return (
            <div className="overview view" style={{ display: "grid" }}>
                <img style={{ maxWidth: 200, margin: "auto", display: "inline-block", marginBottom: 20 }} className="logo" src={logo} alt="Blizzard pro" />
                <Card raised={true} style={{ padding: 20, margin: "auto", display: "inline-block", marginBottom: 20 }} >
                    <CardContent>
                        <h1 style={{ textAlign: "center" }}>Thank you for signing up</h1>
                        <p style={{ textAlign: "center" }}>Your acccount is still under review.<br />You will recieve an email soon with more details.</p>

                        <Button
                            onClick={() => {
                                signOut();
                            }}
                            variant="contained"
                            style={{ width: "48%", marginTop: 50, marginRight: "auto", marginLeft: "auto", display: "block" }}
                            color="primary"
                            className="btn"
                        >
                            Sign out
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    } else if (
        isExpired(currentUser.company) &&
        currentUser.accountLevel.name != "Super Admin"
    ) {
        // Company access has expired (disabled by the expiring-companies cron).
        // This intercepts everyone — including company admins, who would otherwise
        // fall through to the onboarding wizard and could re-sign unsupervised.
        // A company that hasn't used its one-time reactivation gets a button back
        // into the approval workflow for 30 days; once that's spent (the reason
        // carries a "(Second Chance)" marker) it's a sales conversation again.
        const secondChanceAvailable = canTakeSecondChance(currentUser.company);
        // The marker is appended to the original reason — don't show it to the
        // reseller, it's an internal bookkeeping detail.
        const displayReason = (currentUser.company.approvalStatus?.expirationReason || "")
            .replace("(Second Chance)", "")
            .trim();

        return (
            <div className="overview view" style={{ display: "grid" }}>
                <img style={{ maxWidth: 200, margin: "auto", display: "inline-block", marginBottom: 20 }} className="logo" src={logo} alt="Blizzard pro" />
                <Card raised={true} style={{ padding: 20, margin: "auto", display: "inline-block", marginBottom: 20, maxWidth: 640 }} >
                    <CardContent>
                        <h1 style={{ textAlign: "center" }}>Your portal access has expired</h1>
                        {displayReason && (
                            <p style={{ textAlign: "center" }}>{displayReason}.</p>
                        )}

                        {secondChanceAvailable ? (
                            <>
                                <p style={{ textAlign: "center" }}>
                                    We can reopen your account for <strong>30 days</strong> so you can finish
                                    the requirements and keep your reseller status. This is a one-time
                                    reactivation, so please complete the steps before the new deadline.
                                </p>
                                {error.length > 0 && (
                                    <p style={{ textAlign: "center", color: "tomato" }}>{error}</p>
                                )}
                                <Button
                                    onClick={takeSecondChance}
                                    variant="contained"
                                    disabled={reactivating}
                                    style={{ width: "70%", marginTop: 30, marginRight: "auto", marginLeft: "auto", display: "block" }}
                                    color="primary"
                                    className="btn"
                                >
                                    {reactivating ?
                                        <CircularProgress color="inherit" size={24} /> :
                                        "Reactivate My Account"
                                    }
                                </Button>
                            </>
                        ) : (
                            <p style={{ textAlign: "center" }}>
                                We&apos;d love to get you back up and running. Just reach out to us at{" "}
                                <a href="mailto:sales@blizzardpro.com">sales@blizzardpro.com</a> and we&apos;ll help you restore your access.
                            </p>
                        )}

                        <Button
                            onClick={() => {
                                signOut();
                            }}
                            variant={secondChanceAvailable ? "outlined" : "contained"}
                            style={{ width: "48%", marginTop: secondChanceAvailable ? 20 : 50, marginRight: "auto", marginLeft: "auto", display: "block" }}
                            color="primary"
                            className="btn"
                        >
                            Sign out
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    } else if (!currentUser.company.approved && !currentUser.company.provisionalAccess && currentUser.accountLevel.name != "Admin"  && currentUser.accountLevel.name != "Super Admin") {
        return (
            <div className="overview view" style={{ display: "grid" }}>
                <img style={{ maxWidth: 200, margin: "auto", display: "inline-block", marginBottom: 20 }} className="logo" src={logo} alt="Blizzard pro" />
                <Card raised={true} style={{ padding: 20, margin: "auto", display: "inline-block", marginBottom: 20 }} >
                    <CardContent>
                        <h1 style={{ textAlign: "center" }}>You're too early!</h1>
                        <p style={{ textAlign: "center" }}>Your company's portal admin is still setting up the account.<br />Please reach out to your company's account admin to finish setup and get access to the portal.</p>
                        <br /><br />
                        <p style={{ textAlign: "center", fontWeight: "bold" }}>Your comany's admin{companyAdminEmails.length > 1 ? "(s) are:" : " is" }  {companyAdminEmails.join(', ')}</p>

                        <Button
                            onClick={() => {
                                signOut();
                            }}
                            variant="contained"
                            style={{ width: "48%", marginTop: 50, marginRight: "auto", marginLeft: "auto", display: "block" }}
                            color="primary"
                            className="btn"
                        >
                            Sign out
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );

    } else if (!bypassWizard) {
        
        return (
            <AddressProvider>
                <Wizard />
            </AddressProvider>
        );
        // } else if (primaryRoleSetup) {
        //   return (
        //     <InviteCenter tempUser={tempUser} clearTempUser={clearTempUser} user={user} />
        //   );
    } else {
        return (
            <ViewProvider>
                <div className="root">
                    <CssBaseline />
                    <AppBar
                        position="fixed"
                        className={"appBar " + (open ? "appBarShift" : "")}
                    >
                        <Toolbar style={{ minHeight: 48 }}>
                            <div className={open ? "" : "hide"}>
                                <IconButton
                                    style={{
                                        position: "relative",
                                        right: 20,
                                    }}
                                    onClick={handleDrawerClose}
                                >
                                    <MenuOpen
                                        style={{ color: "#828282" }}
                                    />
                                </IconButton>
                            </div>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawerOpen}
                                edge="start"
                                className={"menuButton " + (open ? "hide" : "")}
                            >
                                <Menu />
                            </IconButton>
                            <TopNav />
                        </Toolbar>
                    </AppBar>

                    <Drawer
                        id="main-menu"
                        style={{
                            width: drawerWidth,
                            flexShrink: 0,
                            backgroundColor: "#000",
                            color: "#828282",
                        }}
                        className={"drawer"}
                        variant={mobileMode ? "temporary" : "persistent"}
                        anchor="left"
                        open={open}
                        onClose={mobileMode ? handleDrawerClose : undefined}
                        hideBackdrop={!mobileMode}
                        slotProps={{
                            backdrop: {
                                style: {
                                    width: "100%",
                                    backgroundColor: mobileMode ? 'transparent' : undefined
                                }
                            }
                        }}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                    >
                        <SideDrawer handleMobileEvent={handleMobileEvent} />
                    </Drawer>

                    <main
                        className={"content " + (open ? "contentShift" : "")}
                    >
                        <ApprovalExpirationPopup
                            popupOpen={showExpirationPopup}
                            onClose={closeExpirationPopup}
                        />
                        {onboardingInShell ? <Wizard hideNav /> : (lockedBySecondChance ? buildLockedCard() : children)}
                    </main>
                </div>
            </ViewProvider>
        );
    }
}
